import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Package, Layers, ImageOff } from "lucide-react";
import { formatMeters, stockStatus, statusColor } from "@/lib/format";

export default function EmployeeOrder() {
  const [products, setProducts] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [productId, setProductId] = useState("");
  const [fabricId, setFabricId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [settings, setSettings] = useState({ low_stock_threshold: 10, critical_threshold: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  const loadData = async () => {
    const [{ data: prods }, { data: fabs }, { data: cfg }] = await Promise.all([
      api.get("/products"),
      api.get("/fabrics"),
      api.get("/settings"),
    ]);
    setProducts(prods);
    setFabrics(fabs);
    setSettings(cfg);
  };

  useEffect(() => { loadData(); }, []);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId]
  );

  // When product changes, default fabric to the product's linked fabric (employee can override)
  useEffect(() => {
    if (selectedProduct) {
      setFabricId((current) => current || selectedProduct.fabric_id || "");
    } else {
      setFabricId("");
    }
  }, [selectedProduct]);

  const selectedFabric = useMemo(
    () => fabrics.find((f) => f.id === fabricId),
    [fabrics, fabricId]
  );

  const qty = parseInt(quantity, 10);
  const validQty = Number.isFinite(qty) && qty > 0;
  const metersUsed = selectedProduct && validQty
    ? selectedProduct.meters_per_unit * qty
    : 0;
  const fabricStock = selectedFabric ? selectedFabric.current_stock : 0;
  const remaining = selectedFabric ? Math.max(0, fabricStock) - metersUsed : 0;
  const insufficient = selectedProduct && selectedFabric && validQty && metersUsed > fabricStock;
  const status = selectedFabric
    ? stockStatus(remaining, settings.low_stock_threshold, settings.critical_threshold)
    : "healthy";
  const tone = statusColor(status);

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) return toast.error("Pick a product");
    if (!fabricId) return toast.error("Pick a fabric print");
    if (!validQty) return toast.error("Enter quantity (must be > 0)");
    if (insufficient) return toast.error("Not enough fabric in stock");
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        order_no: orderNo.trim() || null,
        product_id: productId,
        fabric_id: fabricId,
        quantity: qty,
      });
      toast.success(`${data.order_no} confirmed — ${formatMeters(data.meters_deducted)}m deducted`);
      setLastOrder(data);
      setOrderNo("");
      setQuantity("");
      setProductId("");
      setFabricId("");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not confirm order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EF]" data-testid="employee-order-page">
      <Header subtitle="Employee · Order Entry" />

      <main className="max-w-md mx-auto px-5 pt-5 pb-32 space-y-5">
        <div>
          <h1 className="font-heading text-3xl font-bold text-brand-900 tracking-tight">
            New Order
          </h1>
          <p className="text-brand-700/70 mt-1 text-sm">
            Select a product, enter quantity, confirm. Fabric is deducted automatically.
          </p>
        </div>

        {/* Last order confirmation banner */}
        {lastOrder && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-3xl p-4 flex items-start gap-3"
            data-testid="last-order-banner"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900">
                {lastOrder.order_no} confirmed
              </p>
              <p className="text-emerald-700 mt-0.5">
                {lastOrder.product_name} · {lastOrder.quantity} pc → {formatMeters(lastOrder.meters_deducted)}m deducted.
                <br />
                {lastOrder.fabric_name} stock now: <strong>{formatMeters(lastOrder.stock_after)}m</strong>
              </p>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
              Order No. <span className="text-brand-700/50">(optional)</span>
            </Label>
            <Input
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
              placeholder="Leave blank to auto-generate (e.g. ORD-0001)"
              className="h-14 rounded-2xl bg-white border-brand-200 px-5 text-base"
              data-testid="order-no-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
              Product <span className="text-rose-500">*</span>
            </Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger
                className="h-14 rounded-2xl bg-white border-brand-200 px-5 text-base"
                data-testid="product-select-trigger"
              >
                <SelectValue placeholder="Choose product…" />
              </SelectTrigger>
              <SelectContent>
                {products.length === 0 ? (
                  <div className="p-3 text-sm text-brand-700/60">
                    No products yet. Ask admin to add some.
                  </div>
                ) : (
                  products.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`product-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedProduct && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
                Fabric print <span className="text-rose-500">*</span>
              </Label>
              <Select value={fabricId} onValueChange={setFabricId}>
                <SelectTrigger
                  className="h-14 rounded-2xl bg-white border-brand-200 px-5 text-base"
                  data-testid="fabric-select-trigger"
                >
                  <SelectValue placeholder="Choose fabric…" />
                </SelectTrigger>
                <SelectContent>
                  {fabrics.length === 0 ? (
                    <div className="p-3 text-sm text-brand-700/60">
                      No fabrics in inventory.
                    </div>
                  ) : (
                    fabrics.map((f) => {
                      const isDefault = f.id === selectedProduct.fabric_id;
                      return (
                        <SelectItem key={f.id} value={f.id} data-testid={`fabric-option-${f.id}`}>
                          {f.name} · {formatMeters(f.current_stock)}m{isDefault ? " · default" : ""}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {selectedFabric && selectedProduct.fabric_id && selectedFabric.id !== selectedProduct.fabric_id && (
                <p className="text-[11px] text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                  Different from this product's default fabric ({selectedProduct.fabric_name})
                </p>
              )}
            </div>
          )}

          {selectedProduct && selectedFabric && (
            <div className="bg-white rounded-3xl border border-brand-200 p-4 space-y-3" data-testid="auto-fill-card">
              {/* Image row */}
              <div className="flex gap-3">
                <ProductSwatch
                  path={selectedProduct.image_path}
                  fallback={Package}
                  label="Product"
                  testid="auto-product-image"
                />
                <ProductSwatch
                  path={selectedFabric.image_path}
                  fallback={Layers}
                  label="Fabric"
                  testid="auto-fabric-image"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField
                  icon={Layers}
                  label="Fabric"
                  value={selectedFabric.name}
                  testid="auto-fabric-name"
                />
                <ReadOnlyField
                  icon={Package}
                  label="Fabric / unit"
                  value={`${formatMeters(selectedProduct.meters_per_unit)} m`}
                  testid="auto-meters-per-unit"
                />
              </div>
              <p className="text-[11px] text-brand-700/60">
                Meters/unit is locked by admin. Fabric stock: {formatMeters(selectedFabric.current_stock)}m.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
              Quantity <span className="text-rose-500">*</span>
            </Label>
            <Input
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g. 2"
              className="h-14 rounded-2xl bg-white border-brand-200 px-5 text-lg font-medium"
              data-testid="quantity-input"
            />
          </div>

          {/* Live result */}
          {selectedProduct && selectedFabric && validQty && (
            <div
              className={`rounded-3xl border-2 ${tone.ring.replace("ring-", "border-")} ${tone.bg} p-5 space-y-3`}
              data-testid="live-result-card"
            >
              <div className="flex justify-between items-baseline gap-2">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
                  Fabric used
                </span>
                <span className="font-heading text-3xl font-bold text-brand-900 tracking-tight">
                  {formatMeters(metersUsed)} m
                </span>
              </div>
              <div className="flex justify-between items-baseline gap-2 pt-2 border-t border-white/60">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
                  Remaining stock
                </span>
                <span className={`font-heading text-3xl font-bold tracking-tight ${tone.text}`} data-testid="live-remaining">
                  {formatMeters(remaining)} m
                </span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
                <span className={`text-xs font-medium ${tone.text} uppercase tracking-wide`} data-testid="live-status">
                  {status === "healthy" ? "Healthy stock" : status === "low" ? "Low stock" : "Critical — reorder"}
                </span>
              </div>
              {insufficient && (
                <div
                  className="bg-rose-100 border border-rose-200 rounded-2xl p-3 flex items-start gap-2 text-xs text-rose-700"
                  data-testid="insufficient-warning"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
                  <span>
                    Only {formatMeters(selectedFabric.current_stock)}m of {selectedFabric.name} available.
                    Reduce quantity or pick a different fabric.
                  </span>
                </div>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting || !productId || !fabricId || !validQty || insufficient}
            className="w-full h-14 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white text-base font-medium active:scale-[0.98] shadow-sm"
            data-testid="confirm-order-btn"
          >
            {submitting ? "Confirming…" : "Confirm order"}
          </Button>
        </form>
      </main>

      <BottomNav />
    </div>
  );
}

const ReadOnlyField = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-brand-50 rounded-2xl p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-brand-700/70 font-medium">
      <Icon className="w-3 h-3" strokeWidth={2} />
      {label}
    </div>
    <p className="font-heading text-base font-semibold text-brand-900 mt-1 truncate">{value}</p>
  </div>
);

const ProductSwatch = ({ path, fallback: Fallback, label, testid }) => (
  <div className="flex-1" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-brand-700/60 font-medium mb-1.5">
      {label}
    </p>
    <div className="aspect-square rounded-2xl bg-brand-50 overflow-hidden flex items-center justify-center border border-brand-200">
      {path ? (
        <img src={fileUrl(path)} alt={label} className="w-full h-full object-cover" />
      ) : (
        <Fallback className="w-7 h-7 text-brand-700/40" strokeWidth={1.4} />
      )}
    </div>
  </div>
);
